import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, getDoc, query, orderBy, limit, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import EditableContentMultilang from '../components/EditableContentMultilang';
import EditableBackground from '../components/EditableBackground';
import ImageCompareSlider from '../components/ImageCompareSlider';
import { Link } from 'react-router-dom';
import './HomePage.css';
import StaticReviewsSection from '../components/StaticReviewsSection';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    
    const [pageData, setPageData] = useState({
        featuredRooms: [],
        previewServices: [],
        siteSettings: {},
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadHomePageData = useCallback(async () => {
        setError(null);
        try {
            const roomsQuery = query(collection(db, 'rooms'), orderBy('name.it'), limit(3));
            const servicesQuery = query(collection(db, 'services'), orderBy('order', 'asc'), orderBy('title.it', 'asc'), limit(3));
            const settingsDocRef = doc(db, 'site_settings', 'global');

            const [roomsSnapshot, servicesSnapshot, settingsSnap] = await Promise.all([
                getDocs(roomsQuery),
                getDocs(servicesQuery),
                getDoc(settingsDocRef)
            ]);

            const roomsData = roomsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const servicesData = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const settingsData = settingsSnap.exists() ? settingsSnap.data() : { siteName: "L'antico Piol De Feltre" };

            setPageData({
                featuredRooms: roomsData,
                previewServices: servicesData,
                siteSettings: settingsData,
            });

        } catch (err) {
            console.error("Errore nel caricamento dei dati della homepage:", err);
            setError("Impossibile caricare i contenuti della pagina. Riprova più tardi.");
        }
    }, []);

    useEffect(() => {
        loadHomePageData();
    }, [loadHomePageData]);

    useEffect(() => {
        loadHomePageData();
    }, [loadHomePageData]);

    if (error) {
        return <div className="container form-status error" style={{padding: '80px 0'}}>{error}</div>;
    }

    const lang = i18n.language;
    const fallbackLang = 'it';

    return (
                <div className="homepage">
            <>
                <EditableBackground docPath="homepage/hero" field="backgroundImageUrl" defaultImageUrl="/placeholder-image.jpg" className="main-hero-section">
                        <div className="hero-overlay"></div>
                        <div className="hero-content">
                            <EditableContentMultilang docPath="homepage/hero" field="mainTitle" elementType="h1" />
                            <EditableContentMultilang docPath="homepage/hero" field="subtitle" elementType="p" />
                        </div>
                    </EditableBackground>

                    {/* === STATIC REVIEWS WIDGET === */}
                    <StaticReviewsSection />

                    <section className="content-section welcome-section">
                        <div className="container">
                            <EditableContentMultilang docPath="homepage/welcome" field="title" elementType="h2" />
                            <EditableContentMultilang docPath="homepage/welcome" field="paragraph1" elementType="p" />
                            <EditableContentMultilang docPath="homepage/welcome" field="paragraph2" elementType="p" />
                        </div>
                    </section>

                    <section className="content-section featured-rooms-section">
                        <div className="container">
                            <EditableContentMultilang docPath="homepage/featured_rooms" field="title" elementType="h2" />
                            {pageData.featuredRooms.length === 0 ? (
                                <p className="no-content-text">Nessuna camera da mostrare.</p>
                            ) : (
                                <div className="rooms-grid">
                                    {pageData.featuredRooms.map(room => {
                                        const name = room.name?.[lang] || room.name?.[fallbackLang] || 'Nome Camera';
                                        const description = room.shortDescription?.[lang] || room.shortDescription?.[fallbackLang] || 'Descrizione...';
                                        return (
                                            <div key={room.id} className="card room-card">
                                                <img src={room.imageUrl || '/placeholder-image.jpg'} alt={name} className="card-img" onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg' }}/>
                                                <div className="card-body">
                                                    <h3>{name}</h3>
                                                    <p>{description}</p>
                                                    <Link to={`/camere/${room.id}`} className="cta-button details-button-home">{t('buttonSeeDetails')}</Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="text-center" style={{marginTop: '40px'}}>
                                <Link to="/camere" className="cta-button">{t('buttonSeeAllRooms')}</Link>
                            </div>
                        </div>
                    </section>
                    
                    <section className="content-section image-compare-section">
                        <div className="container">
                             <EditableContentMultilang docPath="homepage/slider_section" field="title" elementType="h2" />
                             <EditableContentMultilang docPath="homepage/slider_section" field="subtitle" elementType="p" />
                             <ImageCompareSlider
                                beforeImage="/images/casa-dopo.jpg"
                                afterImage="/images/casa-prima.jpg"
                                beforeLabel={t('beforeLabel')}
                                afterLabel={t('afterLabel')}
                            />
                        </div>
                    </section>

                    <section className="content-section services-preview-section">
                        <div className="container">
                            <EditableContentMultilang docPath="homepage/services_preview" field="title" elementType="h2" />
                            {pageData.previewServices.length === 0 ? (
                                <p className="no-content-text">Nessun servizio da mostrare.</p>
                            ) : (
                                <div className="services-grid">
                                    {pageData.previewServices.map(service => {
                                        const title = service.title?.[lang] || service.title?.[fallbackLang] || 'Servizio';
                                        const description = service.description?.[lang] || service.description?.[fallbackLang] || 'Descrizione...';
                                        return (
                                            <div key={service.id} className="card service-card">
                                                <img src={service.imageUrl || '/placeholder-image.jpg'} alt={title} className="card-img" onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-image.jpg' }}/>
                                                <div className="card-body">
                                                    <h3>{title}</h3>
                                                    <p>{description}</p>
                                                    <Link to={`/servizi/${service.id}`} className="cta-button details-button-home">{t('buttonDiscoverMore')}</Link>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="text-center" style={{marginTop: '40px'}}>
                                <Link to="/servizi" className="cta-button">{t('buttonSeeAllServices')}</Link>
                            </div>
                        </div>
                    </section>
                    
                    <section className="cta-section">
                        <div className="container text-center">
                            <EditableContentMultilang docPath="homepage/cta" field="title" elementType="h2" />
                            <EditableContentMultilang docPath="homepage/cta" field="text" elementType="p" />
                            <a href="/contatti" className="cta-button">{t('buttonContactUs')}</a>
                        </div>
                    </section>
            </>
        </div>
    );
};

export default HomePage;

